/**
 * CancelBetOverlay
 *
 * DOM overlay showing a single red-circle / white-cross button at the
 * bottom-right of a bet zone. Shown when the local player drops bills in
 * their OWN sidebet zone (almost always a misclick meant for an opponent's
 * zone). Clicking cancels: bills return to the wallet accumulator, the zone
 * visual is cleared.
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { worldToScreen } from '../../engine/utils/worldToScreen';

export interface CancelBetOverlayConfig {
  container: HTMLElement;
  camera: THREE.PerspectiveCamera;
  canvas: HTMLCanvasElement;
}

/** World radius of a sidebet zone (matches BetZoneManager DEFAULT_RADIUS). */
const ZONE_RADIUS = 1.1;
/** Fraction of the zone radius used to push the button toward the bottom-right edge. */
const EDGE_OFFSET = 0.75;

export class CancelBetOverlay {
  private container: HTMLElement;
  private camera: THREE.PerspectiveCamera;
  private canvas: HTMLCanvasElement;
  private root: HTMLButtonElement;
  private isVisible = false;
  private onCancel: (() => void) | null = null;
  private anchorWorldPos: THREE.Vector3 | null = null;

  /** Cached button size (fixed 32px in CSS, no media query) — measured once. */
  private btnW = 0;
  private btnH = 0;
  /** Last written position — skip redundant style writes when the camera is still. */
  private lastLeft = NaN;
  private lastTop = NaN;

  constructor(config: CancelBetOverlayConfig) {
    this.container = config.container;
    this.camera = config.camera;
    this.canvas = config.canvas;

    this.root = document.createElement('button');
    this.root.className = 'cancel-bet-overlay';
    this.root.setAttribute('aria-label', 'Cancel bet');
    this.root.innerHTML = `
      <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
        <line x1="7" y1="7" x2="17" y2="17" stroke="#ffffff" stroke-width="3" stroke-linecap="round" />
        <line x1="17" y1="7" x2="7" y2="17" stroke="#ffffff" stroke-width="3" stroke-linecap="round" />
      </svg>
    `;

    this.root.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (this.isVisible) this.onCancel?.();
    });

    this.container.appendChild(this.root);
    this.injectStyles();
  }

  setOnCancel(cb: (() => void) | null): void {
    this.onCancel = cb;
  }

  setAnchorPosition(worldPos: { x: number; y: number; z: number }): void {
    if (!this.anchorWorldPos) {
      this.anchorWorldPos = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    } else {
      this.anchorWorldPos.set(worldPos.x, worldPos.y, worldPos.z);
    }
    this.updateScreenPosition();
  }

  show(): void {
    if (this.isVisible) return;
    this.isVisible = true;
    this.root.classList.add('visible');
    this.root.style.transformOrigin = 'right center';
    this.updateScreenPosition();
    gsap.fromTo(this.root,
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.22, ease: 'back.out(2.0)' },
    );
  }

  hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;
    gsap.to(this.root, {
      scale: 0,
      opacity: 0,
      duration: 0.14,
      ease: 'power2.in',
      onComplete: () => this.root.classList.remove('visible'),
    });
  }

  update(): void {
    if (this.isVisible) this.updateScreenPosition();
  }

  getVisible(): boolean {
    return this.isVisible;
  }

  private updateScreenPosition(): void {
    if (!this.anchorWorldPos) return;

    // Project an anchor offset into the zone's TOP-LEFT quadrant in world
    // space. Camera is angled (positive Y / +Z toward player), so −X projects
    // to the left of the screen-space ellipse and −Z projects upward (away
    // from the camera). Top-left places the button outside the area that
    // tends to be occupied by placed bills + sidebet zones to the right.
    const offsetWorld = {
      x: this.anchorWorldPos.x - ZONE_RADIUS * EDGE_OFFSET,
      y: this.anchorWorldPos.y,
      z: this.anchorWorldPos.z - ZONE_RADIUS * EDGE_OFFSET,
    };
    const screen = worldToScreen(offsetWorld, this.camera, this.canvas);
    if (!screen) return;

    // Button size is fixed in CSS — measure once, then reuse (avoids a forced
    // layout every frame).
    if (this.btnW === 0) this.btnW = this.root.offsetWidth || 32;
    if (this.btnH === 0) this.btnH = this.root.offsetHeight || 32;

    // Shift further left by ~100% of button width so the button sits just
    // outside the zone's left edge rather than overlapping the placed bills.
    const left = screen.x - this.btnW * 1.5;
    const top = screen.y - this.btnH / 2;
    if (left !== this.lastLeft || top !== this.lastTop) {
      this.root.style.left = `${left}px`;
      this.root.style.top = `${top}px`;
      this.lastLeft = left;
      this.lastTop = top;
    }
  }

  private injectStyles(): void {
    if (document.getElementById('cancel-bet-overlay-styles')) return;
    const styles = document.createElement('style');
    styles.id = 'cancel-bet-overlay-styles';
    styles.textContent = `
      .cancel-bet-overlay {
        position: absolute;
        z-index: 45;
        display: none;
        width: 32px;
        height: 32px;
        padding: 0;
        margin: 0;
        border: 2px solid rgba(255, 255, 255, 0.92);
        border-radius: 50%;
        background: radial-gradient(circle at 35% 30%, #ff5b5b, #c61a1a 70%, #8a0d0d 100%);
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.45), 0 0 12px rgba(255, 60, 60, 0.55);
        cursor: pointer;
        pointer-events: auto;
        transform-origin: center;
        will-change: transform, left, top;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }

      .cancel-bet-overlay.visible {
        display: block;
      }

      .cancel-bet-overlay:active {
        filter: brightness(1.25);
      }

      .cancel-bet-overlay svg {
        display: block;
      }
    `;
    document.head.appendChild(styles);
  }

  destroy(): void {
    this.root.remove();
    const styles = document.getElementById('cancel-bet-overlay-styles');
    styles?.remove();
  }
}
